'use client';

export interface LetterTiming {
  letter: string;
  start: number;
  end: number;
  wordIndex: number;
}

interface LetterHighlightedTextProps {
  text: string;
  letterTimings?: LetterTiming[];
  activeLetterIndex: number; // ✅ PERFORMANCE: Now passed as pre-calculated prop
  isPlaying: boolean;
  activeLanguage?: 'source' | 'target';
  showOnlySpelling?: boolean; // For fullscreen spelling mode
  enhancedKidsMode?: boolean; // 🏆 2026 Award-winning kids design
}

export default function LetterHighlightedText({
  text,
  letterTimings,
  activeLetterIndex, // ✅ PERFORMANCE: Received from parent RAF loop
  isPlaying,
  showOnlySpelling = false,
  enhancedKidsMode = false, // NEW: Ultra kid-friendly 2026 design
}: LetterHighlightedTextProps) {
  
  // ✅ PERFORMANCE: Removed useMemo calculation - now done in parent with RAF
  // ✅ PERFORMANCE: Removed all console.logs - massive performance gain
  
  // If no letter timings, fall back to displaying plain text
  if (!letterTimings || letterTimings.length === 0) {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
  }

  // 🚨 CRITICAL FIX 1: Check if text has proper word spacing
  // If text has no spaces (malformed), try to reconstruct from letterTimings
  const wordCount = text.split(' ').length;
  if (wordCount < 2 && text.length > 20) {
    console.warn('[LetterHighlightedText] Text has no spaces - reconstructing from letterTimings');
    const reconstructedText = letterTimings
      .map(t => t.letter || t.char || '')
      .join('');
    // Check if reconstructed text has spaces
    if (reconstructedText.includes(' ')) {
      console.log('[LetterHighlightedText] ✅ Reconstructed text with proper spaces');
      return <span style={{ whiteSpace: 'pre-wrap' }}>{reconstructedText}</span>;
    } else {
      // letterTimings also has no spaces - display what we have
      console.warn('[LetterHighlightedText] ⚠️ letterTimings also missing spaces, using letterTimings as-is');
    }
  }

  // 🚨 CRITICAL FIX 2: If letterTimings doesn't match text length (missing spaces), use text directly!
  // This happens when letter timings generation skips space characters
  if (text && letterTimings.length < text.length * 0.7) {
    console.warn('[LetterHighlightedText] Letter timings too short - using plain text. Text length:', text.length, 'Timings:', letterTimings.length);
    return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
  }

  // SPELLING MODE - Large spaced letters with 2026 kids design
  if (showOnlySpelling) {
    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'clamp(0.5rem, 2vw, 1rem)',
        padding: '1rem',
        whiteSpace: 'pre-wrap',
        position: 'relative',
        background: 'transparent', // ✅ FIXED: No distracting background colors
      }}>

        {letterTimings.map((timing, index) => {
          const isActive = index === activeLetterIndex;
          const char = timing.letter || timing.char || '';
          const isSpace = char === ' ' || char === '\u00A0';

          return (
            <span
              key={index}
              style={{
                fontSize: showOnlySpelling ? 'clamp(3rem, 10vw, 6rem)' : '1em',
                fontWeight: 900,
                display: 'inline-block',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isActive ? 'scale(1.3) translateY(-8px)' : 'scale(1)',
                color: isActive ? '#FFD700' : 'rgba(255, 255, 255, 0.9)',
                textShadow: isActive 
                  ? '0 0 30px rgba(255, 215, 0, 0.8), 0 0 60px rgba(255, 215, 0, 0.4)' 
                  : '0 2px 10px rgba(0, 0, 0, 0.3)',
                filter: isActive ? 'brightness(1.2)' : 'brightness(1)',
                animation: isActive ? 'letterBounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'none',
                minWidth: isSpace ? 'clamp(1rem, 3vw, 2rem)' : 'auto',
                opacity: isSpace ? 0 : 1,
              }}
            >
              {char}
            </span>
          );
        })}

        <style jsx>{`
          @keyframes letterBounce {
            0% {
              transform: scale(0.8) translateY(20px);
              opacity: 0.5;
            }
            50% {
              transform: scale(1.4) translateY(-15px);
            }
            70% {
              transform: scale(1.25) translateY(-5px);
            }
            100% {
              transform: scale(1.3) translateY(-8px);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  // NORMAL MODE - Regular text with letter highlighting
  // 🚨 CRITICAL FIX: Build text from letterTimings BUT if text provided and timings incomplete, use text as source
  const hasCompleteTimings = letterTimings.every((t: any) => t.letter || t.char);
  
  return (
    <span style={{
      lineHeight: 2.4, // ✅ INCREASED: More space between lines for better readability
      display: 'inline',
      whiteSpace: 'pre-wrap',
    }}>
      {hasCompleteTimings ? (
        // Use letterTimings to render (preferred when complete)
        letterTimings.map((timing, index) => {
          const isActive = index === activeLetterIndex;
          const char = timing.letter || timing.char || '';
          const isSpace = char === ' ' || char === '\u00A0';
        
        // ✅ FIX: Check if next character is start of new word (for proper word spacing)
        const nextTiming = letterTimings[index + 1];
        const nextIsSpace = nextTiming ? (nextTiming.letter === ' ' || nextTiming.letter === '\u00A0') : false;
        const isLastLetterOfWord = nextIsSpace || index === letterTimings.length - 1;

        // 🎯 CRITICAL: Handle spaces differently - they need proper width for word separation
        if (isSpace) {
          return (
            <span
              key={index}
              style={{
                display: 'inline-block',
                minWidth: '0.35em',  // ✅ FIX: Normal space width - looks like regular text!
                height: '1em',
              }}
            >
              {'\u00A0'}
            </span>
          );
        }

        return (
              <span
                key={index}
                style={{
                  display: 'inline-block',
                  // 🏆 CLEAN GOLD GRADIENT: Professional, clear, not distracting
                  background: isActive 
                    ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                    : 'transparent',
                  color: isActive ? '#1F2937' : 'inherit', // Dark text on gold for maximum contrast
                  borderRadius: enhancedKidsMode && isActive ? '10px' : '6px',
                  // ✅ BIGGER PADDING: Makes active letter feel like a "button"
                  padding: isActive 
                    ? (enhancedKidsMode ? '6px 10px' : '4px 8px')
                    : '0px 0.5px',  // ✅ FIX: Minimal padding for non-active letters - keeps text tight!
                  // ✅ FIX: NO margin between letters - keep words tight like normal text!
                  marginRight: '0',
                  marginLeft: '0',
                  // ✅ BOLD WEIGHT: Maximum boldness for active letter
                  fontWeight: isActive ? 900 : 'inherit',
                  // ✅ BIGGER FONT: Active letter is also slightly larger in font-size
                  fontSize: isActive && enhancedKidsMode ? '1.15em' : '1em',
                  // ✅ STRONG SHADOW: Triple-layer for maximum visibility
                  boxShadow: isActive 
                    ? '0 6px 20px rgba(255, 215, 0, 0.7), 0 0 28px rgba(255, 165, 0, 0.5), inset 0 2px 6px rgba(255, 255, 255, 0.4)' 
                    : 'none',
                  // ✅ MASSIVE SCALE: 60% bigger! (was 1.5, now 1.6 for enhanced mode)
                  transform: isActive
                    ? (enhancedKidsMode ? 'scale(1.6) translateY(-4px)' : 'scale(1.4) translateY(-3px)')
                    : 'scale(1)',
                  transformOrigin: 'center center',
                  transition: enhancedKidsMode 
                    ? 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                    : 'all 0.25s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                  // ✅ THICK BORDER: Very visible boundary
                  border: isActive
                    ? (enhancedKidsMode ? '3px solid rgba(255, 215, 0, 0.9)' : '2.5px solid rgba(255, 215, 0, 0.8)')
                    : 'none',
                  // ✅ STRONG TEXT SHADOW: Ensures readability
                  textShadow: isActive
                    ? '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 12px rgba(255, 255, 255, 0.6)'
                    : 'none',
                  // ✅ Z-INDEX: Bring active letter to front
                  zIndex: isActive ? 10 : 1,
                  position: 'relative',
                }}
              >
                {char}
              </span>
        );
      })
      ) : (
        // ✅ FALLBACK: If letterTimings incomplete/malformed, use text directly with basic word highlighting
        text.split('').map((char, index) => {
          const isActive = false; // Can't highlight without proper timings
          const isSpace = char === ' ' || char === '\u00A0';
          
          if (isSpace) {
            return (
              <span
                key={index}
                style={{
                  display: 'inline-block',
                  minWidth: '0.35em',
                  height: '1em',
                }}
              >
                {'\u00A0'}
              </span>
            );
          }
          
          return (
            <span
              key={index}
              style={{
                display: 'inline',
              }}
            >
              {char}
            </span>
          );
        })
      )}
    </span>
  );
}

